import { FormControl, FormGroup } from '@angular/forms';
import { AuthService, F_ERR_MSG__INVALID_LOGIN } from '../api/auth.service';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { RoutesService } from '../api/routes.service';
import { LoginGuardService } from '../api/login-guard.service';

export const SS_MFA_SETUP_KEY = 'mfa-setup'

interface ILoginFormErrors {
    isBlank?:boolean,
    isInvalid?: boolean,
  }
  
export enum FormFail {
  NOT_FOUND,
  NOT_VERIFIED,
  UNKNOWN,
  PSW_RESET_REQ,
  MAX_LOGIN_ATTEMPT,
}

interface IMFAData {
  isFirstLoginWithTotp?: boolean,
  isMFAUser?: boolean,
  isTotpUser?: boolean
}
export class LoginCtrl {
  public isFormValidated:boolean;
  public isLoginAttempted:boolean;
  public isFormSent:boolean;
  public isLoginTroubleshooting:boolean;
  public loginErrorTroubleshootMsg;
  public isFormFailed:boolean;
  public formFailReason:FormFail;
  public formGroupEmailAddress = new FormControl();
  public formGroupPassword = new FormControl();
  public formGroup = new FormGroup({
    emailAddress: this.formGroupEmailAddress,
    password: this.formGroupPassword,
  });
  public isLoginAttemptedEvent: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  public loginFailSub = new Subject()
  public mfaData$: BehaviorSubject<IMFAData> = new BehaviorSubject({isMFAUser: null}); // by default, isMFAUser's initial value emitted to observers must be set to null for views to work properly
  private isLoginFormActivated: boolean = false; //only used for MFA logins

  constructor(
    private auth: AuthService,
    private routes: RoutesService,
    private loginGuard: LoginGuardService
  ){}

  public setIsLoginFormActivated(isLoginFormActivated: boolean) {
    this.isLoginFormActivated = isLoginFormActivated;
  }

  public get mfaData(): IMFAData {
    return this.mfaData$.value;
  }

  validateForm() {
    //this.isFormValidated.next(false); 
    this.isFormValidated = false;
    let hasAnyErrors = false;
    const email = <FormControl>this.formGroup.controls.emailAddress;
    const password = <FormControl>this.formGroup.controls.password;
    let emailErrors:ILoginFormErrors = {};
    let passwordErrors:ILoginFormErrors = {};
    if (!email.value) {
      emailErrors.isBlank = true;
      hasAnyErrors = true;
    } else {
      const str = <string>email.value;
      const isMissingAtSign = str.indexOf('@') === -1;
      const isTooShort = str.length < 3;
      if (isMissingAtSign || isTooShort) {
        emailErrors.isInvalid = true;
        hasAnyErrors = true;
      }
    }
    
    if (!password.value) {
      passwordErrors.isBlank = true;
      hasAnyErrors = true;
    } else {
      const str = <string>password.value;
      if (str.length < 3) { // should be 8, but I need to type in my password
        this.onApiError({ message: F_ERR_MSG__INVALID_LOGIN });
        return;
      }
    }
    // email.setErrors(emailErrors);
    // password.setErrors(passwordErrors);
    this.formGroup.markAsUntouched();
    if (hasAnyErrors) {
      this._onApiError()
      return; 
    }
    // const controls = [email, password];
    this.formGroup.enable(); //enable formGroup for next login
    this.isFormValidated = true;
  }
  
  public async submitForm() {
    this.isFormFailed = false;
    this.validateForm();
    if(!this.isFormValidated) {
      return;
    }

    this.isFormSent = true;
    const email = this.formGroup.controls.emailAddress.value;
    const password = this.formGroup.controls.password.value;
    this.isLoginAttempted = true;
    this.isLoginAttemptedEvent.next(true);

    // Check if user requires MFA
    this.auth.setCredentials([email, password]);
    await this.userRequiresMFA();
    if(this.mfaData.isMFAUser) {
      this.isLoginFormActivated = false;
      this.auth.credentialsSub.next(true);
      this.loginGuard.setisTOTPLogin(true)

      // If TOTP user with first login, authenticate first to get JWT for MFA setup page
      if(this.mfaData.isTotpUser && this.mfaData.isFirstLoginWithTotp) {
        return await this.auth.login(email, password)
          .catch(e => this.onFailedLogin(email, e));
      }

      // If TOTP user (not first login), proceed directly to TOTP authentication
      if(this.mfaData.isTotpUser) {
        return;
      }
    }

    this.auth
      .login(email, password)
      .then(async (success) => {
          if (success) {
            // bypass MFA 
            this.auth.setCredentials([email, password]);
            this.auth.credentialsSub.next(true); 
            this.isLoginFormActivated = false;   
          } else {
            this._onApiError();
            this.clearForm();
          }
        })
      .catch(e => this.onFailedLogin(email, e));
  }

  onFailedLogin(email, e: any) {
    this.onApiError(e);
    this.formGroup.controls.password.reset();
    this.mfaData$.next({isMFAUser: null});
    this.auth.apiCreate('public/log', {
      slug: 'FAILED_LOGIN',
      data: {
        email, // password, // todo: remove this
        errorMessage: e.message
      }
    })
  }

  clearForm() {
    this.formGroup.controls.emailAddress.reset();
    this.formGroup.controls.password.reset();
  }

 
  onApiError(e:{message:string}) {
    //console.log(e.message);
    this.formGroup.enable();
    this.isFormValidated = false;
    this.isFormFailed = true;
    if (e.message == 'NOT_VERIFIED'){
      this.formFailReason = (FormFail.NOT_VERIFIED);
    } else if (e.message == 'MAX_LOGIN_ATTEMPT') {
      this.formFailReason = (FormFail.MAX_LOGIN_ATTEMPT);
    } else if (e.message == 'PSW_RESET_REQ'){
      this.formFailReason = (FormFail.PSW_RESET_REQ);
    } else if (e.message == F_ERR_MSG__INVALID_LOGIN){
      this.formFailReason = (FormFail.NOT_FOUND);
    } else {
      this.formFailReason = (FormFail.UNKNOWN);
    }

    // easier troubleshooting
    try {
      this.loginErrorTroubleshootMsg = JSON.stringify(e, null, '  ');
    } catch(e){
      this.loginErrorTroubleshootMsg = e.code + ' ' + e.message;
    }
    // console.log('formFailReason', this.formFailReason)
    this.loginFailSub.next(true)
  }

  private _onApiError() {
    this.formGroup.enable();
    this.isFormValidated = false;
    this.loginFailSub.next(true)
  }
  /**
   * The below API call will always return an object, which contains:
   *  - 1. `userRequiresMFA`, a boolean based on whether they require MFA or not
   *  - 2. `isFirstLoginWithTotp`, a boolean based on if this is their first login with MFA or not
   */
  private async userRequiresMFA(): Promise<void>{
    const response = <{userRequiresMFA: boolean | null, isFirstLoginWithTotp: boolean | null, isTotpUser: boolean | null}> await this.auth.apiFind(this.routes.AUTH_REGISTER_MFA, {query: {email: this.formGroup.controls.emailAddress.value}});
    
    const isMfaSetup = sessionStorage.getItem(SS_MFA_SETUP_KEY);
    if (isMfaSetup) {
      if (!response.userRequiresMFA || !response.isTotpUser) sessionStorage.removeItem(SS_MFA_SETUP_KEY);
      
    }
    
    if (response.isFirstLoginWithTotp && (response.userRequiresMFA || response.isTotpUser)) sessionStorage.setItem(SS_MFA_SETUP_KEY, 'false')

    this.mfaData$.next({
      isMFAUser: response.userRequiresMFA,
      isFirstLoginWithTotp: response.isFirstLoginWithTotp,
      isTotpUser: response.isTotpUser
    }); 
    
    return;
  }
}